/**
 * FitText: the whole text, always, in a box of fixed width (namefit, 3 Oct 2026).
 *
 * Founder, 3 Oct 2026, on "TLF Talentforge (OPC) ..." in the header: "even if
 * the font size is low ... give a predefined width to the name of the client
 * and show the full name of the client in that width". An ellipsis hides the
 * part of a company's name that tells two of them apart ("... Private Limited"
 * against "... LLP"), so this never truncates. It shrinks instead:
 *
 *   1. one line, from `max` down to `oneLineMin`;
 *   2. then two lines, from `max` down to `min`.
 *
 * The first size that fits wins. Sizes are set on the element directly, not
 * through state: measuring and re-rendering in a loop would flash every size
 * on the way down.
 *
 * namesettle (founder, 9 Oct 2026, a photo of the header name "continuous
 * flickering and not settling ... only in some devices"). This used to say the
 * box never changes size, so the ResizeObserver could not feed itself. It does
 * change: one line and two lines are different heights, and in a squeezed
 * header its width is whatever the flex row leaves it, which other things in
 * the row decide. The observer re-fitted on ANY size change, its own result
 * included, so on a device where two sizes kept answering each other it
 * re-fitted every frame and the name never stood still. Our own laptop settled
 * at every width and scale in Chromium, which is why it shipped. So the size
 * is made settle-proof rather than tested device by device:
 *
 *   - only the box's WIDTH re-fits it; a height change (the fit's own answer)
 *     never does;
 *   - two lines are judged against one line measured in the same font, not
 *     against `px * line-height`, which a fallback font with a taller line box
 *     failed at every size, dropping the name to the floor;
 *   - more than MAX_REFITS width changes inside SETTLE_MS is a loop, not a
 *     person, so it stops re-fitting and keeps the size it has. Resizing the
 *     window (a person did that) lets it fit again.
 *
 * @since 1.0.0 (namefit)
 */
import { useLayoutEffect, useRef } from 'react';

const LINE_HEIGHT = 1.15;
/** Width changes allowed inside SETTLE_MS before it stops re-fitting. */
export const MAX_REFITS = 6;
export const SETTLE_MS = 2000;

export function FitText({ text, max = 13, oneLineMin = 10, min = 7, className = '' }: {
    text: string;
    /** The size used when the text fits as it is, in px. */
    max?: number;
    /** Below this one line gives way to two. */
    oneLineMin?: number;
    /** The floor for two lines. */
    min?: number;
    /** Width (and anything else) for the box. Give it a fixed width. */
    className?: string;
}) {
    const ref = useRef<HTMLSpanElement>(null);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        // One line is measured first in every case: two lines fit when they
        // are no taller than two of it.
        const fits = (px: number, two: boolean) => {
            el.style.fontSize = `${px}px`;
            el.style.whiteSpace = 'nowrap';
            const line = el.scrollHeight;
            if (two) el.style.whiteSpace = 'normal';
            return el.scrollWidth <= el.clientWidth + 0.5 && el.scrollHeight <= (two ? 2 : 1) * line + 1;
        };
        // A detached box (unmounted before the fonts came) is 0 wide and left alone.
        let fitted = 0;
        const fit = () => {
            if (!(fitted = el.clientWidth)) return;
            for (let px = max; px >= oneLineMin; px -= 0.5) if (fits(px, false)) return;
            for (let px = oneLineMin; px > min; px -= 0.5) if (fits(px, true)) return;
            fits(min, true);
        };
        // Past MAX_REFITS the count is never reset by time, only by a window resize.
        let since = 0;
        let refits = 0;
        const onResize = () => {
            if (Math.abs(el.clientWidth - fitted) < 1) return;
            const now = performance.now();
            if (refits <= MAX_REFITS && now - since > SETTLE_MS) { since = now; refits = 0; }
            if (++refits <= MAX_REFITS) fit();
        };
        const thaw = () => { refits = 0; onResize(); };

        fit();
        const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(onResize);
        ro?.observe(el);
        window.addEventListener('resize', thaw);
        // The web font arriving changes every width measured before it.
        void document.fonts?.ready.then(fit);
        return () => { ro?.disconnect(); window.removeEventListener('resize', thaw); };
    }, [text, max, oneLineMin, min]);

    return (
        <span ref={ref} className={`block overflow-hidden text-left ${className}`}
            style={{ lineHeight: LINE_HEIGHT, fontSize: max }}>
            {text}
        </span>
    );
}
